import {
    Container,
    Sprite,
    NineSlicePlane
} from 'pixi.js';
import { ActiveButton, Focusable, FocusManager, fonts, StyledBitmapText } from '../../ui';
import { UIBoxBG } from './UIBoxBG';
import KeyboardJS from 'keyboardjs';
import { LoadContext, DisposableGroup, Disposable } from '../../utils';

// width for focus frame
const FRAME_WIDTH = 6;

export class Compass extends Container implements Focusable
{
    private bg: UIBoxBG;
    private label: StyledBitmapText;
    public north: ActiveButton;
    public south: ActiveButton;
    public east: ActiveButton;
    public west: ActiveButton;
    private rose: Sprite;
    private focusBox: NineSlicePlane;
    private disposables: DisposableGroup;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');

        UIBoxBG.preload();
    }

    constructor()
    {
        super();

        this.bg = new UIBoxBG(295, 340);
        this.addChild(this.bg);

        const loader = LoadContext.current;

        this.label = new StyledBitmapText('Send Ping', fonts.GameLabel);
        this.label.anchor.set(0.5, 0.6);
        this.label.position.set(148, 41);
        this.addChild(this.label);

        this.rose = new Sprite(loader.getSpritesheet('GameAtlas').textures['btn_compass']);
        this.rose.position.set(25, 75);
        this.addChild(this.rose);

        const centerX = this.rose.x + this.rose.width / 2;
        const centerY = this.rose.y + this.rose.height / 2;

        // each direction button is a 53px square
        const normalTexture = loader.getSpritesheet('GameAtlas').textures['btn_compass_direction'];
        const activeTexture = loader.getSpritesheet('GameAtlas').textures['btn_active_ping'];
        this.north = new ActiveButton(normalTexture,
            {
                content: 'N',
                style: fonts.CompassNormal
            },
            activeTexture,
            {
                content: 'N',
                style: fonts.CompassActive
            }
        );
        this.north.position.set(centerX, 127 + (66-53)/2);
        this.north.createHitTriangleN();
        this.addChild(this.north);

        this.south = new ActiveButton(normalTexture,
            {
                content: 'S',
                style: fonts.CompassNormal
            },
            activeTexture,
            {
                content: 'S',
                style: fonts.CompassActive
            }
        );
        this.south.position.set(centerX, centerY + (centerY - this.north.y));
        this.south.createHitTriangleS();
        this.addChild(this.south);

        this.east = new ActiveButton(normalTexture,
            {
                content: 'E',
                style: fonts.CompassNormal
            },
            activeTexture,
            {
                content: 'E',
                style: fonts.CompassActive
            }
        );
        this.east.position.set(centerX + (centerY - this.north.y), centerY);
        this.east.createHitTriangleE();
        this.addChild(this.east);

        this.west = new ActiveButton(normalTexture,
            {
                content: 'W',
                style: fonts.CompassNormal
            },
            activeTexture,
            {
                content: 'W',
                style: fonts.CompassActive
            }
        );
        this.west.position.set(centerX - (centerY - this.north.y), centerY);
        this.west.createHitTriangleW();
        this.addChild(this.west);

        this.focusBox = new NineSlicePlane(loader.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const {width, height} = this.bg;
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width + 2;
        this.focusBox.height = height + 2;
        this.focusBox.position.set(-1, -1);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;

        this.north.on('pointertap', () => this.north.enabled ? this.emit('activate', 'N') : null);
        this.south.on('pointertap', () => this.south.enabled ? this.emit('activate', 'S') : null);
        this.east.on('pointertap', () => this.east.enabled ? this.emit('activate', 'E') : null);
        this.west.on('pointertap', () => this.west.enabled ? this.emit('activate', 'W') : null);

        KeyboardJS.withContext('compass', () => {
            this.disposables = new DisposableGroup(
                Disposable.fromKey('left', null, () => this.west.emit('pointertap')),
                Disposable.fromKey('right', null, () => this.east.emit('pointertap')),
                Disposable.fromKey('up', null, () => this.north.emit('pointertap')),
                Disposable.fromKey('down', null, () => this.south.emit('pointertap')),
            );
        });
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            KeyboardJS.setContext('compass');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
            KeyboardJS.setContext('gameplay');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        FocusManager.instance.activateContext([
            this.north,
            this.east,
            this.south,
            this.west
        ]);
    }

    public set enabled(enabled: boolean)
    {
        this.north.enabled = enabled;
        this.south.enabled = enabled;
        this.east.enabled = enabled;
        this.west.enabled = enabled;
    }

    public setActive(direction?:'N'|'S'|'E'|'W')
    {
        this.north.active = direction === 'N';
        this.south.active = direction === 'S';
        this.east.active = direction === 'E';
        this.west.active = direction === 'W';
    }

    public destroy()
    {
        this.disposables.destroy();
        super.destroy({children: true});
    }
}